import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Chip,
  Stack,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  ArrowBack as BackIcon,
  Article as ArticleIcon,
  VideoLibrary as VideoIcon,
  QuestionAnswer as FaqIcon,
  Build as TroubleshootIcon
} from '@mui/icons-material';
import { helpContent, HelpArticle, HelpCategory } from './helpContent';

interface HelpCenterProps {
  open: boolean;
  onClose: () => void;
  initialArticleId?: string;
  initialCategory?: string;
}

const HelpCenter: React.FC<HelpCenterProps> = ({
  open,
  onClose,
  initialArticleId,
  initialCategory
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState<'home' | 'category' | 'article'>('home');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [history, setHistory] = useState<Array<{ view: string; category?: HelpCategory; article?: HelpArticle }>>([]);

  // Initialize with initial article/category if provided
  React.useEffect(() => {
    if (open && initialArticleId) {
      // Find article by ID
      for (const category of helpContent.categories) {
        const article = category.articles.find(a => a.id === initialArticleId);
        if (article) {
          setSelectedCategory(category);
          setSelectedArticle(article);
          setCurrentView('article');
          break;
        }
      }
    } else if (open && initialCategory) {
      const category = helpContent.categories.find(c => c.id === initialCategory);
      if (category) {
        setSelectedCategory(category);
        setCurrentView('category');
      }
    }
  }, [open, initialArticleId, initialCategory]);

  // Search functionality
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    const results: Array<{ category: HelpCategory; article: HelpArticle; score: number }> = [];

    helpContent.categories.forEach(category => {
      category.articles.forEach(article => {
        let score = 0;

        // Title match (highest weight)
        if (article.title.toLowerCase().includes(query)) score += 10;

        // Content match
        if (article.content.toLowerCase().includes(query)) score += 5;

        // Tag match
        if (article.tags?.some(tag => tag.toLowerCase().includes(query))) score += 7;

        // Category match
        if (category.name.toLowerCase().includes(query)) score += 3;

        if (score > 0) {
          results.push({ category, article, score });
        }
      });
    });

    return results.sort((a, b) => b.score - a.score);
  }, [searchQuery]);

  const handleCategoryClick = (category: HelpCategory) => {
    setHistory([...history, { view: currentView, category: selectedCategory || undefined, article: selectedArticle || undefined }]);
    setSelectedCategory(category);
    setCurrentView('category');
    setSearchQuery('');
  };

  const handleArticleClick = (category: HelpCategory, article: HelpArticle) => {
    setHistory([...history, { view: currentView, category: selectedCategory || undefined, article: selectedArticle || undefined }]);
    setSelectedCategory(category);
    setSelectedArticle(article);
    setCurrentView('article');
    setSearchQuery('');
  };

  const handleBack = () => {
    const prev = history[history.length - 1];
    if (prev) {
      setCurrentView(prev.view as any);
      setSelectedCategory(prev.category || null);
      setSelectedArticle(prev.article || null);
      setHistory(history.slice(0, -1));
    } else {
      setCurrentView('home');
      setSelectedCategory(null);
      setSelectedArticle(null);
    }
  };

  const handleHome = () => {
    setCurrentView('home');
    setSelectedCategory(null);
    setSelectedArticle(null);
    setHistory([]);
    setSearchQuery('');
  };

  const handleClose = () => {
    onClose();
    // Reset state after animation
    setTimeout(() => {
      handleHome();
    }, 300);
  };

  const getCategoryIcon = (iconName: string) => {
    const icons: Record<string, React.ReactElement> = {
      article: <ArticleIcon />,
      video: <VideoIcon />,
      faq: <FaqIcon />,
      troubleshoot: <TroubleshootIcon />
    };
    return icons[iconName] || <ArticleIcon />;
  };

  const renderBreadcrumbs = () => {
    return (
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={handleHome}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
          Help Center
        </Link>
        {selectedCategory && (
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setCurrentView('category');
              setSelectedArticle(null);
            }}
            sx={{ cursor: 'pointer' }}
          >
            {selectedCategory.name}
          </Link>
        )}
        {selectedArticle && (
          <Typography color="text.primary" variant="body2">
            {selectedArticle.title}
          </Typography>
        )}
      </Breadcrumbs>
    );
  };

  const renderHome = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        How can we help you?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search for answers or browse categories below
      </Typography>

      {/* Category Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: 2,
        mb: 3
      }}>
        {helpContent.categories.map(category => (
          <Box
            key={category.id}
            onClick={() => handleCategoryClick(category)}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
                transform: 'translateY(-2px)',
                boxShadow: 1
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {getCategoryIcon(category.icon)}
              <Typography variant="h6" sx={{ ml: 1 }}>
                {category.name}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {category.description}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {category.articles.length} articles
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Popular Articles */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Popular Articles
        </Typography>
        <List>
          {helpContent.categories
            .flatMap(cat => cat.articles.map(art => ({ category: cat, article: art })))
            .filter(({ article }) => article.popular)
            .slice(0, 5)
            .map(({ category, article }) => (
              <ListItem key={article.id} disablePadding>
                <ListItemButton onClick={() => handleArticleClick(category, article)}>
                  <ListItemText
                    primary={article.title}
                    secondary={category.name}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Box>
    </Box>
  );

  const renderCategory = () => {
    if (!selectedCategory) return null;

    return (
      <Box>
        {renderBreadcrumbs()}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {getCategoryIcon(selectedCategory.icon)}
          <Typography variant="h5" sx={{ ml: 1 }}>
            {selectedCategory.name}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {selectedCategory.description}
        </Typography>

        <List>
          {selectedCategory.articles.map(article => (
            <ListItem key={article.id} disablePadding>
              <ListItemButton onClick={() => handleArticleClick(selectedCategory, article)}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {article.title}
                      {article.popular && (
                        <Chip label="Popular" size="small" color="primary" />
                      )}
                      {article.videoUrl && (
                        <VideoIcon fontSize="small" color="action" />
                      )}
                    </Box>
                  }
                  secondary={article.description}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const renderArticle = () => {
    if (!selectedArticle || !selectedCategory) return null;

    return (
      <Box>
        {renderBreadcrumbs()}

        <Typography variant="h5" gutterBottom>
          {selectedArticle.title}
        </Typography>

        {selectedArticle.tags && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {selectedArticle.tags.map(tag => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
          </Stack>
        )}

        {selectedArticle.videoUrl && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" icon={<VideoIcon />}>
              <Link href={selectedArticle.videoUrl} target="_blank" rel="noopener">
                Watch video tutorial
              </Link>
            </Alert>
          </Box>
        )}

        <Box
          sx={{
            '& h2': { mt: 3, mb: 2, fontSize: '1.5rem' },
            '& h3': { mt: 2, mb: 1, fontSize: '1.25rem' },
            '& p': { mb: 2 },
            '& ul, & ol': { mb: 2, pl: 3 },
            '& code': {
              bgcolor: 'action.hover',
              p: 0.5,
              borderRadius: 0.5,
              fontFamily: 'monospace'
            },
            '& pre': {
              bgcolor: 'action.hover',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              '& code': { bgcolor: 'transparent', p: 0 }
            },
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 2 }
          }}
          dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Was this helpful? */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Was this article helpful?
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label="Yes"
              onClick={() => console.log('Helpful:', selectedArticle.id)}
              clickable
              color="success"
              variant="outlined"
            />
            <Chip
              label="No"
              onClick={() => console.log('Not helpful:', selectedArticle.id)}
              clickable
              color="error"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Related Articles */}
        {selectedArticle.relatedArticles && selectedArticle.relatedArticles.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Related Articles
            </Typography>
            <List>
              {selectedArticle.relatedArticles.map(relatedId => {
                // Find the related article
                for (const cat of helpContent.categories) {
                  const article = cat.articles.find(a => a.id === relatedId);
                  if (article) {
                    return (
                      <ListItem key={relatedId} disablePadding>
                        <ListItemButton onClick={() => handleArticleClick(cat, article)}>
                          <ListItemText primary={article.title} />
                        </ListItemButton>
                      </ListItem>
                    );
                  }
                }
                return null;
              })}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  const renderSearchResults = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Search Results for "{searchQuery}"
      </Typography>

      {searchResults.length === 0 ? (
        <Alert severity="info">
          No results found. Try different keywords or browse categories below.
        </Alert>
      ) : (
        <List>
          {searchResults.map(({ category, article }) => (
            <ListItem key={article.id} disablePadding>
              <ListItemButton onClick={() => handleArticleClick(category, article)}>
                <ListItemText
                  primary={article.title}
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {category.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {article.description}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(currentView !== 'home' || searchQuery) && (
              <IconButton onClick={searchQuery ? () => setSearchQuery('') : handleBack} size="small">
                <BackIcon />
              </IconButton>
            )}
            <Typography variant="h6">Help Center</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search for help..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </DialogTitle>

      <DialogContent>
        {searchQuery ? renderSearchResults() :
         currentView === 'home' ? renderHome() :
         currentView === 'category' ? renderCategory() :
         renderArticle()}
      </DialogContent>
    </Dialog>
  );
};

export default HelpCenter;
